import { useState } from 'react';
import type { Friend, Group, Identity, Message } from '../types';
import { InstallBanner } from '../components/InstallBanner';
import { errorMessage } from '../lib/db';
import { shareInvite } from '../lib/invite';
import { lookupByUsername } from '../lib/relay';
import { saveFriends } from '../lib/store';

/**
 * Hook that wraps shareInvite() with transient label state so the
 * UI can show feedback ("Copied", "Sent") for a couple of seconds
 * before reverting to the default label.
 */
function useInviteShare(username: string, defaultLabel = 'Share invite link') {
  const [label, setLabel] = useState<string>(defaultLabel);

  async function fire() {
    try {
      const result = await shareInvite(username);
      if (result === 'shared') setLabel('Sent');
      else if (result === 'copied') setLabel('Copied — paste anywhere');
      else return; // cancelled — leave label alone
      window.setTimeout(() => setLabel(defaultLabel), 2400);
    } catch {
      setLabel("Couldn't share. Try long-pressing to copy.");
      window.setTimeout(() => setLabel(defaultLabel), 3200);
    }
  }

  return { label, fire };
}

function EmptyConversations({
  me,
  onOpenQR,
}: {
  me: Identity;
  onOpenQR: () => void;
}) {
  const { label, fire } = useInviteShare(me.username, 'Share invite');

  return (
    <div className="empty-stack">
      <p className="muted">
        No one to write to yet. Pick the route that fits.
      </p>

      <div className="empty-row">
        <button
          type="button"
          className="empty-card empty-card-button"
          onClick={onOpenQR}
        >
          <span className="empty-card-eyebrow">In person</span>
          <span className="empty-card-title">Show your QR</span>
          <span className="empty-card-body">They scan, they're in.</span>
        </button>

        <div className="empty-card">
          <span className="empty-card-eyebrow">By link</span>
          <span className="empty-card-title">Send an invite</span>
          <span className="empty-card-body">A ready-made message with your name.</span>
          <button
            type="button"
            className="btn btn-primary btn-small"
            onClick={fire}
            style={{ marginTop: 12 }}
          >
            {label}
          </button>
        </div>
      </div>

      <p className="empty-footnote">
        Nothing buzzes. You'll see messages when you open the app. Take your time.
      </p>
    </div>
  );
}

type Props = {
  me: Identity;
  friends: Friend[];
  groups: Group[];
  messagesByFriend: Record<string, Message[]>;
  messagesByGroup: Record<string, Message[]>;
  onAddFriend: (f: Friend) => void;
  onOpenFriendConversation: (f: Friend) => void;
  onOpenGroupConversation: (g: Group) => void;
  onOpenInspector: () => void;
  onOpenQR: () => void;
  onCreateGroup: () => void;
  onOpenSettings: () => void;
  onLogout: () => void;
};

export function Home({
  me,
  friends,
  groups,
  messagesByFriend,
  messagesByGroup,
  onAddFriend,
  onOpenFriendConversation,
  onOpenGroupConversation,
  onOpenInspector,
  onOpenQR,
  onCreateGroup,
  onOpenSettings,
  onLogout: _onLogout,
}: Props) {
  const [addInput, setAddInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invite = useInviteShare(me.username, 'Or share invite');

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const username = addInput.trim().toLowerCase();
    if (!username) return;
    if (username === me.username) {
      setError("That's you.");
      return;
    }
    if (friends.some((f) => f.username === username)) {
      setError('Already in your friends.');
      return;
    }
    setBusy(true);
    try {
      const found = await lookupByUsername(username);
      if (!found) {
        setError(`No user named "${username}" is registered yet.`);
        return;
      }
      onAddFriend(found);
      setAddInput('');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function removeFriend(username: string) {
    const next = friends.filter((f) => f.username !== username);
    saveFriends(next);
    window.location.reload();
  }


  return (
    <div className="screen">
      <header className="topbar">
        <div className="brand">
          <span className="brand-dot" aria-hidden="true" />
          <span className="brand-name">No&nbsp;Rush</span>
        </div>
        <div className="topbar-right">
          <span className="me-name">@{me.username}</span>
          <button className="btn btn-ghost btn-small" onClick={onOpenInspector}>
            Inspector
          </button>
          <button className="btn btn-ghost btn-small" onClick={onOpenSettings}>
            Settings
          </button>
        </div>
      </header>

      <main className="content">
        <InstallBanner />

        <section className="card">
          <h2 className="h-section">Add a friend</h2>
          <form onSubmit={add} className="form-inline">
            <input
              type="text"
              value={addInput}
              onChange={(e) => setAddInput(e.target.value)}
              placeholder="bob"
              autoComplete="off"
              maxLength={32}
              disabled={busy}
            />
            <button
              className="btn btn-primary"
              type="submit"
              disabled={busy || !addInput.trim()}
            >
              {busy ? 'Looking up…' : 'Add'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={onOpenQR}>
              Or scan QR
            </button>
            <button type="button" className="btn btn-ghost" onClick={invite.fire}>
              {invite.label}
            </button>
          </form>
          {error && <p className="error">{error}</p>}
        </section>

        <section className="card">
          <div className="card-header-row">
            <h2 className="h-section">Conversations</h2>
            <button
              type="button"
              className="btn btn-ghost btn-small"
              onClick={onCreateGroup}
              disabled={friends.length === 0}
              title={friends.length === 0 ? 'Add a friend first' : 'Make a new small group'}
            >
              + New group
            </button>
          </div>
          {friends.length === 0 && groups.length === 0 ? (
            <EmptyConversations me={me} onOpenQR={onOpenQR} />
          ) : (
            <ul className="friend-list">
              {groups.map((g) => {
                const msgs = messagesByGroup[g.id] ?? [];
                const last = msgs[msgs.length - 1];
                return (
                  <li key={g.id} className="friend-row">
                    <button
                      className="friend-row-main"
                      onClick={() => onOpenGroupConversation(g)}
                    >
                      <span className="friend-avatar group-avatar">
                        {g.name[0]?.toUpperCase() ?? '?'}
                      </span>
                      <span className="friend-text">
                        <span className="friend-name">{g.name}</span>
                        <span className="friend-preview">
                          {last
                            ? `${last.direction === 'out' ? 'you' : last.group_sender ?? '?'}: ${last.body}`
                            : `group · ${g.members.length + 1} people`}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
              {friends.map((f) => {
                const msgs = messagesByFriend[f.username] ?? [];
                const last = msgs[msgs.length - 1];
                return (
                  <li key={f.id} className="friend-row">
                    <button
                      className="friend-row-main"
                      onClick={() => onOpenFriendConversation(f)}
                    >
                      <span className="friend-avatar">
                        {f.username[0]?.toUpperCase() ?? '?'}
                      </span>
                      <span className="friend-text">
                        <span className="friend-name">@{f.username}</span>
                        <span className="friend-preview">
                          {last
                            ? `${last.direction === 'out' ? 'you: ' : ''}${last.body}`
                            : 'no messages yet'}
                        </span>
                      </span>
                    </button>
                    <button
                      className="btn btn-ghost btn-small"
                      onClick={() => removeFriend(f.username)}
                      aria-label={`Remove ${f.username}`}
                    >
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

      </main>
    </div>
  );
}
